import dayjs from 'dayjs';

/**
 * Módulo "Termografia": catálogo de locais, faixa de temperatura, limiar de
 * assimetria e o cálculo das linhas (lado direito, esquerdo e delta T) usado
 * pelo card, pelo formulário e pelo PDF. O catálogo é o mesmo de
 * Thermography::SITES no backend (public/api/thermography/Thermography.php).
 */

// Faixa plausível de temperatura cutânea, em °C (a mesma do backend)
export const TEMP_MIN = 20;
export const TEMP_MAX = 42;
// Temperatura da sala no exame
export const AMBIENT_MIN = 10;
export const AMBIENT_MAX = 40;

/**
 * Limiar único de assimetria térmica entre lados (°C): diferença acima dele é
 * destacada. Fonte e discussão em docs/features/thermography/README.md.
 */
export const ASYMMETRY_THRESHOLD = 0.7;

export const CUSTOM_SITE = 'custom';
export const SIDE_RIGHT = 'right';
export const SIDE_LEFT = 'left';
export const SIDE_LABELS = Object.freeze({ [SIDE_RIGHT]: 'Direito', [SIDE_LEFT]: 'Esquerdo' });

const bi = (key, label) => ({ key, label, bilateral: true });
const one = (key, label) => ({ key, label, bilateral: false });

export const THERMOGRAPHY_REGIONS = Object.freeze([
  {
    key: 'face',
    label: 'Face',
    sites: [one('forehead', 'Fronte'), bi('periorbital', 'Periorbital'), bi('malar', 'Malar'), one('nose', 'Nariz'), bi('masseter', 'Masseter / ATM'), one('chin', 'Mento')],
  },
  {
    key: 'cervical',
    label: 'Cervical',
    sites: [one('neck_anterior', 'Cervical anterior'), one('neck_posterior', 'Cervical posterior'), bi('trapezius', 'Trapézio superior')],
  },
  {
    key: 'thorax',
    label: 'Tórax',
    sites: [one('sternum', 'Esterno'), bi('pectoral', 'Peitoral'), bi('breast', 'Mama')],
  },
  {
    key: 'abdomen',
    label: 'Abdome',
    sites: [one('abdomen_upper', 'Abdome superior'), one('abdomen_lower', 'Abdome inferior'), bi('flank', 'Flanco')],
  },
  {
    key: 'back',
    label: 'Dorsal e lombar',
    sites: [
      bi('scapular', 'Escapular'), one('thoracic_spine', 'Coluna torácica'), bi('lumbar_paravertebral', 'Paravertebral lombar'),
      one('lumbar_spine', 'Coluna lombar'), one('sacral', 'Sacral'), bi('gluteal', 'Glúteo'),
    ],
  },
  {
    key: 'upper_limbs',
    label: 'Membros superiores',
    sites: [
      bi('shoulder', 'Ombro'), bi('arm_anterior', 'Braço anterior'), bi('arm_posterior', 'Braço posterior'), bi('elbow', 'Cotovelo'),
      bi('forearm_anterior', 'Antebraço anterior'), bi('forearm_posterior', 'Antebraço posterior'), bi('wrist', 'Punho'),
      bi('hand_dorsal', 'Mão (dorso)'), bi('hand_palmar', 'Mão (palma)'),
    ],
  },
  {
    key: 'lower_limbs',
    label: 'Membros inferiores',
    sites: [
      bi('hip', 'Quadril'), bi('thigh_anterior', 'Coxa anterior'), bi('thigh_posterior', 'Coxa posterior'), bi('thigh_medial', 'Coxa medial (adutores)'),
      bi('knee_anterior', 'Joelho anterior'), bi('knee_posterior', 'Joelho posterior'), bi('leg_anterior', 'Perna anterior'), bi('calf', 'Panturrilha'),
      bi('ankle', 'Tornozelo'), bi('achilles', 'Tendão calcâneo'), bi('foot_dorsal', 'Pé (dorso)'), bi('foot_plantar', 'Pé (planta)'),
    ],
  },
]);

export const CUSTOM_REGION = Object.freeze({ key: CUSTOM_SITE, label: 'Outros locais' });

const SITE_INDEX = new Map();
THERMOGRAPHY_REGIONS.forEach((region) => region.sites.forEach((site) => SITE_INDEX.set(site.key, { ...site, region })));

export const getSite = (key) => SITE_INDEX.get(key) || null;

// ─── Números ────────────────────────────────────────────────────────────────

const round1 = (value) => Math.round(value * 10) / 10;

/** Texto digitado (vírgula ou ponto) para número com uma casa; null se vazio ou inválido. */
export function parseTemp(text) {
  if (text == null) return null;
  if (typeof text === 'number') return Number.isFinite(text) ? round1(text) : null;
  const clean = String(text).trim().replace(',', '.');
  if (clean === '' || !/^-?\d+(\.\d+)?$/.test(clean)) return null;
  return round1(parseFloat(clean));
}

export const isTempInRange = (value) => value != null && value >= TEMP_MIN && value <= TEMP_MAX;
export const isAmbientInRange = (value) => value != null && value >= AMBIENT_MIN && value <= AMBIENT_MAX;

export const formatTemp = (value) => (value == null ? '—' : Number(value).toFixed(1).replace('.', ','));

/** Delta T com sinal (direito menos esquerdo). */
export function formatDelta(delta) {
  if (delta == null) return '—';
  const text = Math.abs(delta).toFixed(1).replace('.', ',');
  if (delta > 0) return `+${text}`;
  if (delta < 0) return `−${text}`;
  return text;
}

export const formatThreshold = () => formatTemp(ASYMMETRY_THRESHOLD);

export const isAsymmetric = (delta) => delta != null && Math.abs(delta) > ASYMMETRY_THRESHOLD;

/** Delta T (direito menos esquerdo) com uma casa. */
export const deltaOf = (right, left) => round1(right - left);

// ─── Linhas da avaliação ────────────────────────────────────────────────────

const customId = (label) => `${CUSTOM_SITE}:${String(label || '').trim().toLowerCase()}`;

/** Identidade de uma linha da tabela: o local (os dois lados ficam na mesma linha). */
export const rowIdOf = (reading) => (reading.site === CUSTOM_SITE ? customId(reading.label) : reading.site);

/** Agrupa as leituras por linha: { id, label, bilateral, right, left, value, delta, asymmetric }. */
function collectRows(readings) {
  const rows = new Map();
  (Array.isArray(readings) ? readings : []).forEach((reading) => {
    if (!reading || reading.value == null) return;
    const site = reading.site === CUSTOM_SITE ? null : getSite(reading.site);
    if (reading.site !== CUSTOM_SITE && !site) return;
    const id = rowIdOf(reading);
    const row = rows.get(id) || {
      id,
      regionKey: site ? site.region.key : CUSTOM_SITE,
      label: site ? site.label : String(reading.label || '').trim(),
      bilateral: site ? site.bilateral : false,
      right: null,
      left: null,
      value: null,
    };
    const value = Number(reading.value);
    if (reading.side === SIDE_RIGHT) row.right = value;
    else if (reading.side === SIDE_LEFT) row.left = value;
    else row.value = value;
    // Local livre com lado vira par
    if (!site && reading.side) row.bilateral = true;
    rows.set(id, row);
  });

  rows.forEach((row) => {
    row.delta = row.right != null && row.left != null ? deltaOf(row.right, row.left) : null;
    row.asymmetric = isAsymmetric(row.delta);
  });
  return rows;
}

const regionOrder = (key) => {
  const index = THERMOGRAPHY_REGIONS.findIndex((region) => region.key === key);
  return index < 0 ? THERMOGRAPHY_REGIONS.length : index;
};

const siteOrder = (row) => {
  if (row.regionKey === CUSTOM_SITE) return 0;
  const region = THERMOGRAPHY_REGIONS.find((item) => item.key === row.regionKey);
  return region ? region.sites.findIndex((site) => site.key === row.id) : 0;
};

/**
 * Linhas da avaliação agrupadas por região, na ordem do catálogo, com os
 * valores da avaliação anterior (`previous`) do mesmo local quando houver.
 * Devolve [{ region: { key, label }, rows: [...] }].
 */
export function buildGroups(readings, previousReadings) {
  const current = collectRows(readings);
  const previous = previousReadings ? collectRows(previousReadings) : null;

  const rows = [...current.values()].map((row) => ({ ...row, previous: previous ? previous.get(row.id) || null : null }));
  rows.sort((a, b) => regionOrder(a.regionKey) - regionOrder(b.regionKey) || siteOrder(a) - siteOrder(b) || a.label.localeCompare(b.label));

  const groups = [];
  rows.forEach((row) => {
    let group = groups[groups.length - 1];
    if (!group || group.region.key !== row.regionKey) {
      const region = THERMOGRAPHY_REGIONS.find((item) => item.key === row.regionKey) || CUSTOM_REGION;
      group = { region: { key: region.key, label: region.label }, rows: [] };
      groups.push(group);
    }
    group.rows.push(row);
  });
  return groups;
}

export const countAsymmetries = (groups) => groups.reduce((total, group) => total + group.rows.filter((row) => row.asymmetric).length, 0);
export const countSites = (groups) => groups.reduce((total, group) => total + group.rows.length, 0);

// ─── Histórico ──────────────────────────────────────────────────────────────

export const sortByDateDesc = (list) => [...(Array.isArray(list) ? list : [])]
  .sort((a, b) => dayjs(b.measuredAt).valueOf() - dayjs(a.measuredAt).valueOf() || Number(b.id) - Number(a.id));

export function findOwnRecord(list, appointmentId) {
  if (appointmentId == null) return null;
  return (Array.isArray(list) ? list : []).find((item) => String(item.appointmentId) === String(appointmentId)) || null;
}

/** Avaliação imediatamente anterior a `record` na lista ordenada (mais recente primeiro). */
export function previousOf(sorted, record) {
  if (!record) return null;
  const index = sorted.findIndex((item) => String(item.id) === String(record.id));
  return index >= 0 ? sorted[index + 1] || null : null;
}

export const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '');
